import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const principles = [
  {
    id: 1,
    icon: 'Bell',
    title: 'Préavis',
    summary: 'Transparence sur les données collectées et leur usage',
    content: `Nous informons les personnes concernées des données personnelles que nous collectons et de la manière dont elles seront utilisées. Nos responsabilités spécifiques en matière de protection des informations personnelles varient selon les différentes lois et réglementations relatives à la protection de la confidentialité. Toutefois, nos responsabilités éthiques et juridiques peuvent être résumées par plusieurs principes clés de confidentialité.

Concrètement, avant toute collecte de données, vous êtes informé(e) :
• Du type de données collectées (nom, adresse, coordonnées, historique de services)
• De la finalité de cette collecte (gestion des interventions, facturation, communication)
• Des tiers susceptibles d'y avoir accès (techniciens, partenaires de paiement)
• De vos droits en tant que personne concernée`,
  },
  {
    id: 2,
    icon: 'CheckSquare',title: 'Choix et consentement',summary: 'Votre accord, votre contrôle sur l\'utilisation de vos données',
    content: `Nous utilisons nos principes de confidentialité comme lignes directrices pour garantir le respect des droits et des préférences des personnes dont nous traitons les données.

Nous donnons aux personnes concernées le choix de la collecte, de l'utilisation et du partage de leurs données. Lorsque cela est nécessaire, nous obtenons au préalable le consentement des personnes concernées.

Vous disposez du droit de :
• Accepter ou refuser la collecte de certaines catégories de données non essentielles
• Retirer votre consentement à tout moment, sans que cela affecte la licéité du traitement antérieur
• Vous opposer à l'utilisation de vos données à des fins de marketing ou de communication commerciale
• Paramétrer vos préférences de notifications et de communications`,
  },
  {
    id: 3,
    icon: 'Database',
    title: 'Collecte et conservation',
    summary: 'Minimisation des données — uniquement ce qui est nécessaire',
    content: `Nous ne collectons et n'utilisons les données que de manière conforme à nos objectifs déclarés. Nous ne collectons que les données strictement nécessaires à un objectif précis et ne les conservons que le temps requis. Ensemble, ces pratiques sont appelées « minimisation des données ».

Données collectées dans le cadre de nos services :
• Informations d'identification : nom, prénom, adresse e-mail, numéro de téléphone
• Adresse d'intervention pour la planification des déplacements
• Historique des services pour le suivi des garanties et la maintenance préventive
• Informations de paiement traitées de manière sécurisée via nos partenaires certifiés

Durées de conservation :
• Données de compte actif : durée de la relation contractuelle
• Données de facturation : 10 ans (obligation légale)
• Données de contact marketing : 3 ans après le dernier contact
• Données techniques de connexion : 12 mois`,
  },
  {
    id: 4,
    icon: 'Download',title: 'Accès et portabilité',summary: 'Consultez et exportez vos données personnelles à tout moment',
    content: `Sur demande, nous donnons aux personnes concernées la possibilité de consulter les données personnelles collectées à leur sujet. Nous permettons aux personnes concernées d'obtenir une copie de leurs données personnelles ou de les transférer à une autre organisation.

Comment exercer votre droit d'accès :
• Connectez-vous à votre espace client et accédez à la section « Mes données »
• Envoyez une demande écrite à : privacy@plumberpro.fr
• Nous répondons à toute demande dans un délai maximum de 30 jours

Portabilité des données :
• Vous pouvez demander l'export de vos données dans un format structuré et lisible (JSON, CSV)
• Ces données peuvent être transmises directement à un autre prestataire de services sur votre demande
• L'export inclut : historique des interventions, préférences, informations de contact`,
  },
  {
    id: 5,
    icon: 'Edit3',title: 'Correction et suppression',summary: 'Rectifiez ou effacez vos informations personnelles',
    content: `Nous donnons aux personnes concernées la possibilité de corriger les données personnelles qui sont inexactes. Sur demande et sous certaines restrictions, nous supprimons toutes les données personnelles collectées concernant une personne.

Droit de rectification :
• Modifiez directement vos informations depuis votre espace client
• Signalez toute inexactitude à notre équipe via privacy@plumberpro.fr
• Les corrections sont appliquées dans un délai de 72 heures

Droit à l'effacement (« droit à l'oubli ») :
• Vous pouvez demander la suppression de vos données lorsqu'elles ne sont plus nécessaires
• Certaines données peuvent être conservées pour des obligations légales (facturation, garanties)
• La suppression est confirmée par écrit dans un délai de 30 jours
• Les données anonymisées à des fins statistiques ne sont pas concernées par ce droit`,
  },
  {
    id: 6,
    icon: 'Shield',
    title: 'Sécurité',
    summary: 'Protection de vos données contre tout accès non autorisé',
    content: `Nous assurons la sécurité en protégeant les données personnelles que nous traitons contre le vol, la perte, l'utilisation abusive ou la divulgation non autorisée.

Mesures techniques et organisationnelles :
• Chiffrement TLS/SSL pour toutes les transmissions de données
• Chiffrement AES-256 pour le stockage des données sensibles
• Authentification à deux facteurs pour l'accès aux systèmes internes • Contrôle d'accès strict basé sur les rôles (principe du moindre privilège)

Surveillance et réponse aux incidents :
• Surveillance continue de nos systèmes 24h/24, 7j/7
• Procédure de notification en cas de violation de données (72h max selon le RGPD)
• Audits de sécurité réguliers réalisés par des tiers indépendants
• Formation obligatoire de tous nos collaborateurs aux bonnes pratiques de sécurité

Partenaires certifiés :
• Paiements traités via Stripe (certifié PCI DSS niveau 1)
• Infrastructure hébergée sur des serveurs conformes ISO 27001`,
  },
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => setOpenId(openId === id ? null : id);

  return (
    <>
      <Helmet>
        <title>Politique de Confidentialité | PlumberPro</title>
        <meta
          name="description"
          content="Politique de confidentialité de PlumberPro — comment nous collectons, utilisons et protégeons vos données personnelles conformément au RGPD."
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-primary/80 shadow-md">
                <Icon name="Droplet" size={24} color="#FFFFFF" />
              </div>
              <span className="text-xl font-semibold text-foreground">PlumberPro</span>
            </button>
            <Button variant="outline" size="sm" iconName="ArrowLeft" onClick={() => navigate('/')}>
              Retour
            </Button>
          </div>
        </header>

        <main className="pt-16">
          {/* Hero */}
          <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-background py-20 px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Icon name="Shield" size={16} />
                <span>Conformité RGPD</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
                Politique de<br />
                <span className="text-primary">Confidentialité</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">
                Chez PlumberPro, la protection de vos données personnelles est une priorité. Découvrez comment nous respectons vos droits à travers nos six engagements fondamentaux.
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                Dernière mise à jour : 1er avril 2026
              </p>
            </div>
          </section>

          {/* Intro */}
          <section className="max-w-3xl mx-auto px-4 py-10">
            <div className="bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground leading-relaxed">
              <p>
                Nos responsabilités spécifiques en matière de protection des informations personnelles varient selon les différentes lois et réglementations relatives à la protection de la confidentialité. Toutefois, nos responsabilités éthiques et juridiques peuvent être résumées par les six principes clés de confidentialité présentés ci-dessous. Nous utilisons ces principes comme lignes directrices pour garantir le respect des droits et des préférences des personnes dont nous traitons les données.
              </p>
            </div>
          </section>

          {/* Accordion Principles */}
          <section className="max-w-3xl mx-auto px-4 pb-16 space-y-3">
            {principles?.map((p) => {
              const isOpen = openId === p?.id;
              return (
                <div
                  key={p?.id}
                  className={`border rounded-2xl overflow-hidden transition-all duration-200 ${
                    isOpen ? 'border-primary/40 shadow-md shadow-primary/5' : 'border-border'
                  } bg-card`}
                >
                  <button
                    onClick={() => toggle(p?.id)}
                    className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                        isOpen ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
                      }`}
                    >
                      <Icon name={p?.icon} size={20} color={isOpen ? '#FFFFFF' : undefined} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Principe {p?.id}
                        </span>
                      </div>
                      <h2 className="text-base font-semibold text-foreground">{p?.title}</h2>
                      <p className="text-sm text-muted-foreground truncate">{p?.summary}</p>
                    </div>
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-200 ${
                        isOpen ? 'rotate-180 bg-primary/10' : 'bg-muted'
                      }`}
                    >
                      <Icon name="ChevronDown" size={16} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 pt-1 border-t border-border/50">
                      <div className="pl-14">
                        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                          {p?.content}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          {/* Contact DPO */}
          <section className="max-w-3xl mx-auto px-4 pb-16">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="Mail" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Exercer vos droits
              </h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                Pour toute question relative à vos données personnelles ou pour exercer vos droits (accès, rectification, suppression, portabilité), contactez notre délégué à la protection des données.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="mailto:privacy@plumberpro.fr"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Icon name="Mail" size={16} color="#FFFFFF" />
                  privacy@plumberpro.fr
                </a>
                <a
                  href="tel:+33123456789"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                >
                  <Icon name="Phone" size={16} />
                  01 23 45 67 89
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-8">
          <div className="max-w-3xl mx-auto px-4 text-center text-sm text-muted-foreground">
            <p>© 2026 PlumberPro. Tous droits réservés.</p>
            <p className="mt-1">
              Cette politique est conforme au Règlement Général sur la Protection des Données (RGPD) — Règlement (UE) 2016/679.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicy;
